class WeeklyDeals extends HTMLElement {
  connectedCallback() {
    this.peels = new Map();
    this.resizeObservers = [];
    this.initCountdown();
    this.initCopyButtons();

    // Defer peel.js init until layout is fully settled — peel.js reads
    // offsetWidth/Height in its constructor, and if it runs before CSS
    // applies, the back-layer transforms end up enormous.
    const ready = () => requestAnimationFrame(() => this.initPeels());
    if (document.readyState === 'complete') {
      ready();
    } else {
      window.addEventListener('load', ready, { once: true });
    }
  }

  disconnectedCallback() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    if (this.resizeObservers) {
      this.resizeObservers.forEach((ro) => ro.disconnect());
      this.resizeObservers = [];
    }
  }

  initPeels() {
    if (typeof window.Peel === 'undefined') return;
    const peelEls = this.querySelectorAll('[data-peel]');
    peelEls.forEach((el) => {
      const button = el.closest('[data-copy-code]');
      if (!button) return;
      try {
        const peel = new window.Peel(el, {
          corner: window.Peel.Corners.TOP_RIGHT,
          backShadow: true,
          bottomShadow: false,
          backReflection: true,
          // Skip peel.js's auto-init at the exact corner — the library has
          // a degenerate-line bug there that clips a diagonal off the cover.
          setPeelOnInit: false,
        });

        const applyPath = () => {
          // peel.js stores dimensions internally; reapply them so they
          // track the current rendered size of the .peel element.
          peel.setupDimensions();
          peel.setCorner(window.Peel.Corners.TOP_RIGHT);
          // peel.js can't render a perfectly flat (zero-peel) state — its
          // math always assumes some peel exists. Start with a tiny fold at
          // the top-right corner and animate past the opposite corner.
          peel.setPeelPath(
            peel.width * 0.93,
            peel.height * 0.07,
            -peel.width * 0.4,
            peel.height * 1.4
          );
          peel.setTimeAlongPath(button.dataset.revealed === 'true' ? 1 : 0);
          peel.setFadeThreshold(0.85);
        };

        applyPath();
        this.peels.set(button, peel);

        // Re-apply on resize so the cover always covers the whole button.
        if (typeof ResizeObserver !== 'undefined') {
          let lastW = el.offsetWidth;
          let lastH = el.offsetHeight;
          const ro = new ResizeObserver(() => {
            const w = el.offsetWidth;
            const h = el.offsetHeight;
            if (w === lastW && h === lastH) return;
            if (w === 0 || h === 0) return;
            lastW = w;
            lastH = h;
            applyPath();
          });
          ro.observe(el);
          this.resizeObservers.push(ro);
        }
      } catch (e) {
        console.warn('peel.js init failed', e);
      }
    });
  }

  animatePeel(peel, duration = 650) {
    return new Promise((resolve) => {
      const start = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        peel.setTimeAlongPath(ease(t));
        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(tick);
    });
  }

  initCountdown() {
    const countdown = this.querySelector('[data-countdown]');
    if (!countdown) return;

    const date = this.dataset.endDate;
    const time = this.dataset.endTime || '23:59';
    if (!date) return;

    const target = new Date(`${date}T${time}:00`);
    if (isNaN(target.getTime())) return;

    const els = {
      root: countdown,
      days: countdown.querySelector('[data-days]'),
      hours: countdown.querySelector('[data-hours]'),
      minutes: countdown.querySelector('[data-minutes]'),
      seconds: countdown.querySelector('[data-seconds]'),
      ended: countdown.querySelector('[data-countdown-ended]'),
    };

    const tick = () => {
      const diff = target.getTime() - Date.now();
      if (diff <= 0) {
        els.root.dataset.ended = 'true';
        if (els.ended) els.ended.hidden = false;
        clearInterval(this.countdownTimer);
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      if (els.days) els.days.textContent = String(days).padStart(2, '0');
      if (els.hours) els.hours.textContent = String(hours).padStart(2, '0');
      if (els.minutes) els.minutes.textContent = String(minutes).padStart(2, '0');
      if (els.seconds) els.seconds.textContent = String(seconds).padStart(2, '0');
    };

    tick();
    this.countdownTimer = setInterval(tick, 1000);
  }

  initCopyButtons() {
    const buttons = this.querySelectorAll('[data-copy-code]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.handleCopy(btn));
    });
  }

  async handleCopy(btn) {
    const code = btn.dataset.copyCode;
    if (!code) return;

    const isFirstReveal = btn.dataset.revealed !== 'true';

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        this.fallbackCopy(code);
      }
    } catch (e) {
      this.fallbackCopy(code);
    }

    const showCopied = () => {
      btn.dataset.copied = 'true';
      const success = btn.querySelector('.weekly-deals__code-action-success');
      if (success) success.hidden = false;

      if (btn._resetTimer) clearTimeout(btn._resetTimer);
      btn._resetTimer = setTimeout(() => {
        btn.dataset.copied = 'false';
        if (success) success.hidden = true;
      }, 2400);
    };

    if (isFirstReveal) {
      const peel = this.peels && this.peels.get(btn);
      if (peel) {
        await this.animatePeel(peel, 700);
      }
      btn.dataset.revealed = 'true';
      showCopied();
    } else {
      showCopied();
    }
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (e) {
      // noop
    }
    document.body.removeChild(textarea);
  }
}

if (!customElements.get('weekly-deals')) {
  customElements.define('weekly-deals', WeeklyDeals);
}
